import { useQuery } from "@tanstack/react-query";
import api from "../../services/api";

const PAGE_LIMIT = 100;

const extractItems = (body) => {
  if (!body || typeof body !== "object") return [];

  if (Array.isArray(body)) return body;
  if (Array.isArray(body.data)) return body.data;

  const nested = body.data;
  if (nested && typeof nested === "object") {
    const keys = [
      "subindustries",
      "subIndustries",
      "subindustry",
      "subIndustry",
      "items",
      "results",
    ];
    for (const key of keys) {
      if (Array.isArray(nested[key])) return nested[key];
    }
  }

  const topKeys = ["subindustries", "subIndustries", "subindustry", "subIndustry"];
  for (const key of topKeys) {
    if (Array.isArray(body[key])) return body[key];
  }

  return [];
};

/** Response: { success, data: [...], pagination } */
const parseListResponse = (body) => {
  const items = extractItems(body);
  const pagination = body?.pagination || body?.data?.pagination || {};

  return {
    items,
    hasNextPage: pagination.hasNextPage === true,
    totalPages: pagination.totalPages ?? pagination.pages ?? 1,
    total: pagination.total ?? pagination.totalItems ?? items.length,
  };
};

const fetchAllPages = async (path, extraParams = {}) => {
  let page = 1;
  let allItems = [];

  while (true) {
    const res = await api.get(path, {
      params: { page, limit: PAGE_LIMIT, ...extraParams },
    });
    const { items, hasNextPage } = parseListResponse(res.data);
    allItems = allItems.concat(items);
    if (!hasNextPage || items.length === 0) break;
    page += 1;
  }

  return allItems;
};

const parseIndustries = (body) => {
  if (!body || typeof body !== "object") return [];
  if (Array.isArray(body.data)) return body.data;
  if (Array.isArray(body)) return body;
  return (
    body.data?.industry ||
    body.data?.industries ||
    body.industry ||
    body.industries ||
    []
  );
};

const sortByNewest = (list) =>
  [...list].sort((a, b) => {
    const idA = a._id || a.id || "";
    const idB = b._id || b.id || "";
    return String(idB).localeCompare(String(idA));
  });

const fetchSubIndustriesByIndustry = async () => {
  const industries = await fetchAllPages("/industry/all");

  const batches = await Promise.all(
    industries.map(async (ind) => {
      const industryId = ind._id || ind.id;
      if (!industryId) return [];

      try {
        const subs = await fetchAllPages(`/subindustry/all/${industryId}`);
        const industryName = ind.title || ind.name || "";
        return subs.map((sub) => ({
          ...sub,
          industryName:
            sub.industryName || sub.parentIndustry?.title || industryName,
        }));
      } catch (err) {
        console.error(
          `Failed to fetch subindustries for industry ${industryId}:`,
          err,
        );
        return [];
      }
    }),
  );

  return sortByNewest(batches.flat());
};

const fetchSubIndustriesFromList = async (params = {}) => {
  if (params.page) {
    const res = await api.get("/subindustry/list", { params });
    return parseListResponse(res.data);
  }

  const items = await fetchAllPages("/subindustry/list", params);
  return { items, hasNextPage: false, totalPages: 1, total: items.length };
};

/**
 * Paginated sub-industry list for the table page.
 * Uses GET /subindustry/list (single request) instead of N requests per industry.
 */
export const useSubIndustriesPageQuery = ({
  page = 1,
  limit = 8,
  search = "",
} = {}) => {
  const trimmedSearch = search.trim();

  return useQuery({
    queryKey: ["subIndustriesList", page, limit, trimmedSearch],
    queryFn: async () => {
      const params = { page, limit };
      if (trimmedSearch) params.search = trimmedSearch;

      try {
        const result = await fetchSubIndustriesFromList(params);
        return {
          items: result.items,
          totalPages: Math.max(1, result.totalPages),
          total: result.total,
        };
      } catch (listErr) {
        console.warn(
          "subindustry/list failed, falling back to per-industry fetch:",
          listErr,
        );

        const allItems = await fetchSubIndustriesByIndustry();
        const filtered = trimmedSearch
          ? allItems.filter((item) =>
              String(item.title || item.name || "")
                .toLowerCase()
                .includes(trimmedSearch.toLowerCase()),
            )
          : allItems;

        const start = (page - 1) * limit;
        return {
          items: filtered.slice(start, start + limit),
          totalPages: Math.max(1, Math.ceil(filtered.length / limit)),
          total: filtered.length,
        };
      }
    },
    staleTime: 60_000,
    placeholderData: (previousData) => previousData,
  });
};

/** Sub-industries for one parent industry */
export const useViewSubIndustryQuery = (id) => {
  return useQuery({
    queryKey: ["viewSubIndustry", id],
    queryFn: async () => {
      const items = await fetchAllPages(`/subindustry/all/${id}`);
      return { data: items };
    },
    enabled: !!id,
  });
};

/** Load all sub-industries (for exports / legacy use) */
export const useAllSubIndustriesQuery = () => {
  return useQuery({
    queryKey: ["allSubIndustries"],
    queryFn: async () => {
      try {
        const { items } = await fetchSubIndustriesFromList();
        return items;
      } catch {
        return fetchSubIndustriesByIndustry();
      }
    },
    select: sortByNewest,
    staleTime: 60_000,
  });
};
